import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  SubscriptionPlanCode,
  SubscriptionPlanSummary,
  SubscriptionTier,
} from '../../models/Subscription';
import { formatPlanPercent, formatPlanPrice } from './subscriptionPlanUi';

export interface SubscriptionPlanCardProps {
  plan: SubscriptionPlanSummary;
  accent: string;
  /** Marks the live subscription plan (success border / Current chip). */
  isCurrent?: boolean;
  isPopular?: boolean;
  /** Private org offer (e.g. early adopter) — not an active subscription by itself. */
  isPrivateOffer?: boolean;
  /** Stronger border/shadow emphasis (upgrade or popular). */
  isHighlighted?: boolean;
  /** Trial-oriented price block (signup / marketing / active trial). */
  onboarding?: boolean;
  /**
   * Active trial on the billing page — price reads
   * “Free for 30 days (Until {{date}})” then the monthly amount.
   */
  trialUntilDate?: string | null;
  /** Show trial pricing even when `trialUntilDate` is still loading. */
  activeTrial?: boolean;
  canSelect?: boolean;
  actionLabel: string;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  actionStartIcon?: React.ReactNode;
  onSelect?: () => void;
  /**
   * Replaces the default CTA button. Pass `null` to hide the action row
   * (e.g. current plan when actions live in a sibling panel).
   */
  actionSlot?: React.ReactNode | null;
  /** Extra row under the title (status caption, etc.). */
  meta?: React.ReactNode;
  /** Chips pinned to the card’s top-right corner. */
  cornerBadges?: React.ReactNode;
  /** Soften card when shown as current in a comparison grid. */
  muted?: boolean;
  /** Stretch to fill a grid cell; current-plan hero can leave this false. */
  fullHeight?: boolean;
}

function TierIcon({ tier }: { tier: SubscriptionTier }) {
  switch (tier) {
    case SubscriptionTier.PRO:
      return <TrendingUpIcon />;
    case SubscriptionTier.MEDIUM:
      return <SchoolIcon />;
    default:
      return <StarIcon />;
  }
}

/**
 * Shared plan card chrome used by the catalogue picker and the current-plan summary.
 */
const SubscriptionPlanCard: React.FC<SubscriptionPlanCardProps> = ({
  plan,
  accent,
  isCurrent = false,
  isPopular = false,
  isPrivateOffer = false,
  isHighlighted = false,
  onboarding = false,
  trialUntilDate = null,
  activeTrial = false,
  canSelect = false,
  actionLabel,
  actionLoading = false,
  actionDisabled = false,
  actionStartIcon,
  onSelect,
  actionSlot,
  meta,
  cornerBadges,
  muted = false,
  fullHeight = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const planCodeLabel = (code: SubscriptionPlanCode) =>
    t(`subscription.planCodes.${code}`, code.replace(/_/g, ' '));

  const showTrialPricing = onboarding || activeTrial || Boolean(trialUntilDate);
  return (
    <Card
      role={canSelect ? 'button' : undefined}
      tabIndex={canSelect ? 0 : undefined}
      aria-disabled={canSelect ? false : undefined}
      aria-label={canSelect ? actionLabel : undefined}
      onClick={canSelect ? onSelect : undefined}
      onKeyDown={
        canSelect
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
      sx={{
        height: fullHeight ? '100%' : 'auto',
        position: 'relative',
        overflow: 'visible',
        cursor: canSelect ? 'pointer' : 'default',
        border: isCurrent
          ? `2px solid ${theme.palette.success.main}`
          : isHighlighted
            ? `2px solid ${accent}`
            : `1.5px solid ${alpha(accent, 0.45)}`,
        boxShadow: isHighlighted
          ? `0 0 0 1px ${alpha(accent, 0.25)}, 0 10px 28px ${alpha(accent, 0.18)}`
          : theme.shadows[2],
        transform: isHighlighted ? 'scale(1.02)' : 'scale(1)',
        opacity: muted ? 0.85 : 1,
        transition: 'all 0.3s ease',
        bgcolor: isCurrent
          ? alpha(theme.palette.success.main, 0.06)
          : isHighlighted
            ? alpha(accent, 0.06)
            : alpha(accent, 0.02),
        '&:hover': {
          transform: canSelect || isHighlighted ? 'scale(1.02)' : 'scale(1)',
          boxShadow: isHighlighted
            ? `0 0 0 1px ${alpha(accent, 0.35)}, 0 14px 32px ${alpha(accent, 0.28)}`
            : canSelect
              ? theme.shadows[8]
              : theme.shadows[2],
          borderColor: isCurrent ? theme.palette.success.main : accent,
        },
        '&:focus-visible': {
          outline: `2px solid ${accent}`,
          outlineOffset: 3,
        },
      }}
    >
      {isCurrent && !meta && !cornerBadges ? (
        <Chip
          label={t('subscription.currentPlan', 'Current')}
          color="success"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        />
      ) : isPrivateOffer ? (
        <Chip
          label={t('subscription.privateOffer', 'Special offer')}
          color="warning"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        />
      ) : isPopular ? (
        <Chip
          label={t('subscription.mostPopular', 'Most Popular')}
          color="secondary"
          size="small"
          sx={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        />
      ) : null}

      {cornerBadges ? (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.75,
          }}
        >
          {cornerBadges}
        </Box>
      ) : null}

      <CardContent
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          mb={1.5}
          gap={1.5}
          sx={{ pr: cornerBadges ? 12 : 0 }}
        >
          <Box
            sx={{
              color: accent,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <TierIcon tier={plan.tier} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h5" component="h3" fontWeight="bold" lineHeight={1.2}>
              {planCodeLabel(plan.code)}
            </Typography>
          </Box>
        </Box>

        {meta ? <Box sx={{ mb: 2 }}>{meta}</Box> : null}

        <Box mb={2}>
          {showTrialPricing ? (
            <>
              <Typography
                variant="h5"
                component="div"
                fontWeight="bold"
                color="success.main"
                lineHeight={1.2}
              >
                {t('subscription.onboarding.priceFreeTrial', 'Free for 30 days')}
              </Typography>
              {trialUntilDate ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('subscription.priceTrialUntil', 'Until {{date}}', { date: trialUntilDate })}
                </Typography>
              ) : null}
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75, fontWeight: 600 }}>
                {t('subscription.onboarding.priceThen', 'Then {{price}}/month', {
                  price: formatPlanPrice(plan.amount, plan.currencyCode),
                })}
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="h3"
                component="div"
                fontWeight="bold"
                color="primary"
                sx={{ fontSize: { xs: '2rem', sm: '2.5rem' } }}
              >
                {formatPlanPrice(plan.amount, plan.currencyCode)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('subscription.perMonthly', 'per month')}
              </Typography>
            </>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1} sx={{ flexGrow: 1, mb: 3 }}>
          {onboarding && (
            <Box display="flex" alignItems="flex-start" gap={1}>
              <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
              <Typography variant="body2" fontWeight={600}>
                {t('subscription.onboarding.trialFeature', '1-month free trial — cancel anytime')}
              </Typography>
            </Box>
          )}
          <Box display="flex" alignItems="flex-start" gap={1}>
            <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
            <Typography variant="body2">
              {t('subscription.commissions.online', 'Online booking commission: {{percent}}', {
                percent: formatPlanPercent(plan.onlineBookingCommissionPercent),
              })}
            </Typography>
          </Box>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
            <Typography variant="body2">
              {t(
                'subscription.commissions.outside',
                'Outside-platform booking commission: {{percent}}',
                {
                  percent: formatPlanPercent(plan.outsidePlatformBookingCommissionPercent),
                },
              )}
            </Typography>
          </Box>
        </Stack>

        {actionSlot !== undefined ? (
          actionSlot
        ) : (
          <Button
            variant={isCurrent ? 'outlined' : 'contained'}
            fullWidth
            disabled={actionDisabled}
            tabIndex={-1}
            startIcon={
              actionLoading
                ? undefined
                : isCurrent
                  ? (actionStartIcon ?? <CheckCircleIcon />)
                  : actionStartIcon
            }
            sx={
              isCurrent
                ? {
                    mt: 'auto',
                    fontWeight: 700,
                    pointerEvents: 'none',
                    color: theme.palette.success.main,
                    borderColor: alpha(theme.palette.success.main, 0.45),
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    '&.Mui-disabled': {
                      color: theme.palette.success.main,
                      borderColor: alpha(theme.palette.success.main, 0.45),
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                    },
                  }
                : {
                    mt: 'auto',
                    fontWeight: 700,
                    pointerEvents: 'none',
                    backgroundColor: accent,
                    borderColor: accent,
                    color: theme.palette.getContrastText(accent),
                  }
            }
          >
            {actionLoading ? <CircularProgress size={22} color="inherit" /> : actionLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionPlanCard;
